import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, SectionHeading } from '@/components/ui';
import { Field, inputClass } from '@/components/forms/form';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

const LANGUAGES = [
  'Hindi',
  'English',
  'Tamil',
  'Telugu',
  'Bengali',
  'Marathi',
  'Punjabi',
  'Urdu',
  'Kannada',
  'Malayalam',
  'Gujarati',
  'Odia',
];

/** Guard rails on what a companion may charge. See B4 in the backlog. */

interface FormState {
  displayName: string;
  city: string;
  languages: string[];
  headline: string;
  bio: string;
}

const EMPTY: FormState = {
  displayName: '',
  city: '',
  languages: [],
  headline: '',
  bio: '',
};

/**
 * Lets a companion write their own shopfront.
 *
 * Writes across two tables: `profiles` (name, city, languages — things true of
 * the person) and `companion_profiles` (headline, bio, specialties, rates —
 * things true of the service). RLS permits both for the owner; the privilege
 * guard blocks status and revenue share regardless of what is sent.
 */
export default function CompanionProfileEditor() {
  const { profile, refreshProfile } = useAuth();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [initial, setInitial] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!profile) return;
    setLoading(true);

    const { data, error: err } = await supabase
      .from('companion_profiles')
      .select('headline, bio')
      .eq('id', profile.id)
      .maybeSingle();

    if (err) setError(err.message);

    const next: FormState = {
      displayName: profile.display_name ?? '',
      city: profile.city ?? '',
      languages: profile.languages ?? [],
      headline: data?.headline ?? '',
      bio: data?.bio ?? '',
    };

    setForm(next);
    setInitial(next);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const toggle = (value: string) =>
    set(
      'languages',
      form.languages.includes(value)
        ? form.languages.filter((v) => v !== value)
        : [...form.languages, value],
    );

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const nameOk = form.displayName.trim().length >= 2;
  const langsOk = form.languages.length > 0;
  const canSave = dirty && nameOk && langsOk && !saving;

  const save = async () => {
    if (!canSave || !profile) return;
    setSaving(true);
    setError('');

    // profiles first — if this fails, nothing else has changed yet.
    const { error: pErr } = await supabase
      .from('profiles')
      .update({
        display_name: form.displayName.trim(),
        city: form.city.trim() || null,
        languages: form.languages,
      })
      .eq('id', profile.id);

    if (pErr) {
      setError(pErr.message);
      setSaving(false);
      return;
    }

    // Status and revenue share are deliberately absent — the privilege guard
    // would reject them, and they are not the companion's to set.
    const { error: cErr } = await supabase
      .from('companion_profiles')
      .update({
        headline: form.headline.trim() || null,
        bio: form.bio.trim() || null,
      })
      .eq('id', profile.id);

    if (cErr) {
      setError(cErr.message);
      setSaving(false);
      return;
    }

    await refreshProfile();
    setInitial(form);
    setSaved(true);
    setSaving(false);
  };

  if (loading) {
    return <p className="py-20 text-center text-sm text-[var(--muted)]">Loading…</p>;
  }

  return (
    <div className="animate-rise mx-auto flex max-w-3xl flex-col gap-10">
      <header>
        <Link
          to="/companion"
          className="text-sm text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
        >
          ← Dashboard
        </Link>
        <p className="eyebrow mt-4">Your profile</p>
        <h1 className="mt-2 text-3xl font-semibold">How members see you</h1>
        <p className="mt-2 text-[var(--ink-2)]">
          This is your entire shopfront. Someone decides whether to call you from it.
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl border-l-[3px] px-4 py-3 text-sm"
          style={{ borderColor: 'var(--danger)', background: 'var(--danger-soft)' }}
        >
          {error}
        </div>
      )}

      {/* ── Who you are ──────────────────────────────────────────────────── */}
      <Card className="p-6">
        <SectionHeading>Who you are</SectionHeading>

        <div className="flex flex-col gap-5">
          <Field
            id="displayName"
            label="Display name"
            hint="What members see. Your legal name is never shown."
            error={nameOk ? undefined : 'At least two characters.'}
          >
            <input
              id="displayName"
              className={inputClass}
              value={form.displayName}
              maxLength={60}
              onChange={(e) => set('displayName', e.target.value)}
            />
          </Field>

          <Field id="city" label="City" hint="Optional. Helps members feel closer to you.">
            <input
              id="city"
              className={inputClass}
              placeholder="Pune"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
            />
          </Field>

          <div>
            <span className="mb-2 block text-sm font-medium">
              Languages you can hold a full conversation in
            </span>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => {
                const on = form.languages.includes(l);
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggle(l)}
                    aria-pressed={on}
                    className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150 hover:-translate-y-px"
                    style={{
                      background: on ? 'var(--marigold-soft)' : 'var(--surface)',
                      borderColor: on ? 'var(--marigold)' : 'var(--line)',
                      color: on ? 'var(--marigold)' : 'var(--ink-2)',
                    }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            {!langsOk && (
              <p className="mt-2 text-xs" style={{ color: 'var(--danger)' }}>
                Pick at least one.
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* ── What you offer ───────────────────────────────────────────────── */}
      <Card className="p-6">
        <SectionHeading>What you offer</SectionHeading>

        <div className="flex flex-col gap-5">
          <Field
            id="headline"
            label="One line about you"
            hint="Shown under your name in the directory. This does most of the work."
          >
            <input
              id="headline"
              className={inputClass}
              maxLength={120}
              placeholder="Here to listen, without judgement"
              value={form.headline}
              onChange={(e) => set('headline', e.target.value)}
            />
          </Field>

          <Field
            id="bio"
            label="In your words"
            hint={`${form.bio.length}/2000 · Say who you are and what you are good at hearing.`}
          >
            <textarea
              id="bio"
              rows={5}
              maxLength={2000}
              className={`${inputClass} resize-none`}
              placeholder="Most people who call me just want to be heard at the end of a long day…"
              value={form.bio}
              onChange={(e) => set('bio', e.target.value)}
            />
          </Field>

          {/*
            The "Good to talk about" tag picker lived here, writing to the
            free-text `specialties` array. The service catalogue replaced it —
            a fixed taxonomy that search and filtering can actually rely on.
          */}
        </div>
      </Card>

      {/* ── Rates ────────────────────────────────────────────────────────── */}
      {/*
        This card used to edit chat_rate_coins / audio_rate_coins /
        video_rate_coins. Since the pivot nothing reads those columns — a rate
        belongs to a (service, booking type) pair in companion_service_rates,
        which is what booking actually charges against.

        So a companion could set prices here, save successfully, and change
        nothing at all. A form that reports success and has no effect is worse
        than a missing form: they had no reason to look further.
      */}
      <Card className="p-6">
        <SectionHeading eyebrow="Pricing">Your rates live with your services</SectionHeading>
        <p className="text-[var(--ink-2)]">
          You set a price for each service and each way someone can book it — chat, voice, video
          or meeting in person — because they are rarely worth the same.
        </p>
        <div className="mt-5">
          <Button to="/companion/services">Manage services and prices</Button>
        </div>
      </Card>

      {/* ── Save ─────────────────────────────────────────────────────────── */}
      <div className="sticky bottom-4 flex flex-wrap items-center gap-4">
        <Button size="lg" onClick={() => void save()} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>

        {saved && !dirty && <Badge tone="online">Saved — you are live in the directory</Badge>}
        {dirty && !saving && <span className="text-sm text-[var(--muted)]">Unsaved changes</span>}
      </div>
    </div>
  );
}
